import type { Country } from '@kizen/api/geography';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired } from './types';

import { getCountries } from '@kizen/api/geography';
import { getResource } from './resource';
import { curry1 } from './utils';

type CountriesQuery<TData = Country[]> = WithRequired<
  QueryObserverOptions<
    Country[],
    DefaultError,
    TData,
    Country[],
    [Resource<Readonly<['geography', 'countries']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const countriesQuery = <TData = Country[]>(
  axios: AxiosInstance,
  options?: Omit<CountriesQuery<TData>, 'queryFn' | 'queryKey'>
): CountriesQuery<TData> => {
  const path = ['geography', 'countries'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getCountries(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public countriesQuery: <TData = Country[]>(
    ...args: Drop<Parameters<typeof countriesQuery<TData>>>
  ) => ReturnType<typeof countriesQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.countriesQuery = curry1(countriesQuery)(axios);
  }
}

export type GeographyQueries = Queries;

export const defGeographyQueries = (axios: AxiosInstance): GeographyQueries => {
  return new Queries(axios);
};
